import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../services/api';
import { Share2, Copy, Check, Clock, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const EXPIRY_OPTIONS = [
  { id: '24h', label: '24 hours' },
  { id: '7d',  label: '7 days' },
  { id: '30d', label: '30 days' },
];

export default function ShareFieldDialog({ open, onClose }) {
  const [step, setStep] = useState('compose');           // 'compose' | 'done'
  const [fields, setFields] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [expiry, setExpiry] = useState('24h');
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [share, setShare] = useState(null);
  const [advisor, setAdvisor] = useState(null);
  const [assignToAdvisor, setAssignToAdvisor] = useState(() => {
    const saved = localStorage.getItem('assagart_assign_to_advisor');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('assagart_assign_to_advisor', String(assignToAdvisor));
  }, [assignToAdvisor]);

  useEffect(() => {
    if (!open) return;
    setStep('compose');
    setSelected(new Set());
    setRecipientEmail('');
    setRecipientName('');
    setPurpose('');
    setExpiry('24h');
    setShareUrl('');
    // assignToAdvisor preserved across opens to respect user preference
    api.listShareableFields()
      .then(r => setFields(r.data.fields))
      .catch(() => toast.error('Could not load field list'));
    api.getMyAdvisor()
      .then(r => {
        const adv = r.data?.advisor || null;
        setAdvisor(adv);
        if (adv?.email && !recipientEmail) setRecipientEmail(adv.email);
        if (adv?.full_name && !recipientName) setRecipientName(adv.full_name + (adv.company ? `, ${adv.company}` : ''));
      })
      .catch(() => setAdvisor(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleField = (key) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const handleCreate = async () => {
    if (selected.size === 0) { toast.error('Pick at least one field to share'); return; }
    if (!recipientEmail.trim()) { toast.error('Add a recipient email'); return; }
    setBusy(true);
    try {
      const r = await api.createPiiShare({
        recipient_email: recipientEmail.trim(),
        recipient_name: recipientName.trim() || null,
        fields: [...selected],
        expiry,
        purpose: purpose.trim() || null,
        assigned_partner_id: advisor && assignToAdvisor ? advisor.id : null,
      });
      setShare(r.data);
      // Backend returns relative '/share/<token>' - prepend origin
      const url = `${window.location.origin}/share/${r.data.share_url.split('/').pop()}`;
      setShareUrl(url);
      setStep('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create share link');
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-xl" data-testid="share-field-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <Share2 size={18} className="text-gold" />
            Share encrypted fields
          </DialogTitle>
        </DialogHeader>

        {step === 'compose' && (
          <>
            <div className="bg-gold/5 border border-gold/20 rounded p-3 mb-3 text-[11px] text-zinc-300 leading-relaxed flex items-start gap-2">
              <ShieldCheck size={14} className="text-gold flex-shrink-0 mt-0.5" />
              <p>
                We will generate a one-time secure URL. The fields you pick will be decrypted exactly once,
                only when the recipient opens it. The link expires automatically and can be revoked at any time.
              </p>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <Label className="text-muted-foreground text-xs">Fields to share</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                  {fields.map(f => (
                    <button
                      key={f.key}
                      type="button"
                      disabled={!f.has_value}
                      onClick={() => toggleField(f.key)}
                      data-testid={`share-field-${f.key}`}
                      className={`flex items-center justify-between gap-2 px-3 py-2 rounded border text-[11px] text-left transition-colors ${
                        !f.has_value
                          ? 'opacity-40 border-[#262626] cursor-not-allowed'
                          : selected.has(f.key)
                            ? 'bg-gold/15 border-gold/40 text-gold'
                            : 'bg-transparent border-[#262626] text-white hover:border-white/40'
                      }`}
                    >
                      <span className="truncate">{f.label}</span>
                      {selected.has(f.key) && <Check size={12} />}
                      {!f.has_value && <span className="text-[9px] uppercase tracking-wider opacity-70">empty</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-muted-foreground text-xs">Recipient email</Label>
                  <Input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => { const v = e.target.value; setRecipientEmail(v); }}
                    className="luxury-input"
                    placeholder="broker@example.com"
                    data-testid="share-recipient-email"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Recipient name (optional)</Label>
                  <Input
                    value={recipientName}
                    onChange={(e) => { const v = e.target.value; setRecipientName(v); }}
                    className="luxury-input"
                    placeholder="John Smith, Aon"
                    data-testid="share-recipient-name"
                  />
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground text-xs">Purpose (optional)</Label>
                <Input
                  value={purpose}
                  onChange={(e) => { const v = e.target.value; setPurpose(v); }}
                  className="luxury-input"
                  placeholder="Policy underwriting for Birkin 30"
                  data-testid="share-purpose"
                />
              </div>

              <div>
                <Label className="text-muted-foreground text-xs flex items-center gap-1">
                  <Clock size={11} /> Expiry
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  {EXPIRY_OPTIONS.map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setExpiry(opt.id)}
                      data-testid={`share-expiry-${opt.id}`}
                      className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded border transition-colors ${
                        expiry === opt.id
                          ? 'bg-gold/15 text-gold border-gold/40'
                          : 'bg-transparent text-muted-foreground border-[#262626] hover:border-white/20 hover:text-white'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>

              {advisor && (
                <label
                  htmlFor="share-assign-advisor"
                  className="flex items-start gap-3 rounded border border-gold/25 bg-gold/5 p-3 cursor-pointer hover:bg-gold/10 transition-colors"
                  data-testid="share-advisor-toggle"
                >
                  <input
                    id="share-assign-advisor"
                    type="checkbox"
                    checked={assignToAdvisor}
                    onChange={(e) => setAssignToAdvisor(e.target.checked)}
                    className="mt-0.5 accent-gold"
                    data-testid="share-advisor-checkbox"
                  />
                  <div className="text-[11px] leading-snug">
                    <span className="text-gold">Also notify my advisor</span>
                    <span className="block text-muted-foreground mt-0.5">
                      {advisor.full_name}{advisor.company ? ` - ${advisor.company}` : ''} will see this share in their inbox.
                    </span>
                  </div>
                </label>
              )}
            </div>

            <DialogFooter className="gap-2 flex-row flex-wrap sm:justify-end">
              <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button
                type="button"
                onClick={handleCreate}
                disabled={busy}
                className="bg-gradient-to-r from-gold to-amber-300 text-black font-semibold"
                data-testid="share-create-btn"
              >
                {busy ? 'Generating...' : 'Generate secure link'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'done' && (
          <div className="space-y-4" data-testid="share-success">
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded p-3 flex items-start gap-2">
              <Check size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-emerald-300 text-sm font-medium">Secure link generated</p>
                <p className="text-[11px] text-zinc-400 mt-1">
                  Copy the URL below and send it to <span className="text-white">{recipientEmail}</span>.
                  They have one view, valid until {new Date(share?.expires_at).toLocaleString()}.
                </p>
              </div>
            </div>

            <div className="bg-[#0A0A0A] border border-[#262626] rounded p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Share URL</p>
              <div className="flex items-center gap-2">
                <code className="text-xs text-white break-all flex-1" data-testid="share-url">{shareUrl}</code>
                <Button type="button" size="sm" onClick={copyUrl} className="bg-gold/15 hover:bg-gold/25 text-gold" data-testid="share-copy-btn">
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
              </div>
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3 flex items-start gap-2 text-[11px] text-amber-100/80">
              <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <p>
                We deliberately do not email the recipient on your behalf. Send the link via the channel
                you trust most (encrypted email, signal, in person). Anyone with the link can view the
                fields once.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" onClick={onClose} className="bg-gradient-to-r from-gold to-amber-300 text-black font-semibold">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
